import { useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Loader2 } from "lucide-react";
import { api, errText } from "../../lib/api";
import { fmtDay } from "../../lib/litters";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Checkbox } from "../ui/checkbox";
import { Textarea } from "../ui/textarea";

// Inline "Due Dec 1 · change" control for when babies come early or late
export const DueDateEditor = ({ litter, lang, onSaved }) => {
  const fr = lang === "fr";
  const clutch = litter.kind === "clutch";
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(litter.expected_date || "");
  const [tell, setTell] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!val) return toast.error(fr ? "Choisissez une date" : "Pick a date");
    setBusy(true);
    try {
      const { data } = await api.patch(`/my/litters/${litter.id}/due-date`, { expected_date: val, notify: tell, note });
      toast.success(data.arrived ? (fr ? "Date mise à jour — marquée comme arrivée" : "Date updated, now showing as arrived") : (fr ? "Date mise à jour" : "Due date updated"),
        data.notified ? { description: fr ? `Votre liste d'attente (${data.waitlist_count}) sera avisée.` : `Your waitlist (${data.waitlist_count}) is being told.` } : undefined);
      if (data.notify_error) toast.warning(data.notify_error);
      setOpen(false); onSaved();
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };
  const label = clutch ? (fr ? "Éclosion" : "Hatch date") : (fr ? "Date prévue" : "Due date");
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setVal(litter.expected_date || ""); }}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`litter-due-${litter.id}`} className="text-[11px] font-semibold inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-slate-200 text-slate-600 hover:border-[#F5551E] hover:text-[#F5551E] transition-colors">
          <CalendarDays size={12} /> {litter.expected_date ? `${label}: ${fmtDay(litter.expected_date, lang)}` : (fr ? "Ajouter une date prévue" : `Add ${label.toLowerCase()}`)}
          <span className="text-[#F5551E]">· {fr ? "modifier" : "change"}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 bg-white" align="start" data-testid={`litter-due-pop-${litter.id}`}>
        <p className="text-sm font-semibold text-[#12234A]">{fr ? "Arrivée en avance ou en retard?" : "Coming early or late?"}</p>
        <p className="text-xs text-slate-500 mt-0.5">{fr ? "Le badge « Arrivé » s'affiche automatiquement à cette date." : "The \"Arrived\" badge shows automatically from this date."}</p>
        <Input type="date" value={val} onChange={(e) => setVal(e.target.value)} className="mt-3 h-10 rounded-xl" data-testid={`litter-due-input-${litter.id}`} />
        {litter.waitlist_count > 0 && (
          <div className="mt-3 rounded-xl bg-slate-50 p-2.5" data-testid={`litter-due-tell-box-${litter.id}`}>
            <label className="flex items-center gap-2 text-sm text-[#12234A] cursor-pointer">
              <Checkbox checked={tell} onCheckedChange={(v) => setTell(!!v)} data-testid={`litter-due-tell-${litter.id}`} />
              {fr ? `Aviser ma liste d'attente (${litter.waitlist_count})` : `Tell my waitlist (${litter.waitlist_count})`}
            </label>
            {tell && <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={400} rows={2} className="mt-2 rounded-lg text-sm bg-white"
              placeholder={fr ? "Note facultative, ex. Maman va bien, encore un peu de patience!" : "Optional note, e.g. Mom is doing great, just a little longer!"} data-testid={`litter-due-note-${litter.id}`} />}
          </div>
        )}
        <Button onClick={save} disabled={busy} className="mt-3 w-full h-9 rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white" data-testid={`litter-due-save-${litter.id}`}>
          {busy && <Loader2 size={14} className="animate-spin mr-1.5" />}{fr ? "Enregistrer la date" : "Save date"}
        </Button>
      </PopoverContent>
    </Popover>
  );
};
